import type { EnrichmentAttributeGroup } from './types';

/**
 * Two small helpers shared by the enrichment-run panel's group adapters, over the
 * panel's own `EnrichmentAttributeGroup`/`EnrichmentAttributeOption` shape rather
 * than any one source's wire format: slugging a group name into a stable-ish key,
 * and looking up an attribute's label by key.
 *
 * This file used to also hold `adaptContentAttributes`, the config-service
 * adapter — hence the old filename, `adapt-content-attributes.ts`. That adapter
 * was deleted once its only caller, `use-enrichment-attribute-groups.ts`, switched
 * the panel onto the google agent's own catalog (`adapt-google-catalog.ts`), which
 * left this file with no adapter at all — just the two generic helpers below,
 * hence the rename. `toGroupSlug` is used by the meta adapter
 * (`~/features/content-enrichment/utils/adapt-meta-catalog.ts`); `labelForKey` by
 * the panel itself.
 */

/**
 * Group name → slug. The config service returns per-environment UUIDs as group
 * ids and no stable key, so the name is the only cross-environment identity
 * available; slugging it keeps the rule maps readable (`conversational`) instead
 * of pinning them to an id that changes per deployment.
 */
export function toGroupSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Label for an attribute key, used by the locked-reason copy so the UI never
 * shows a raw wire key to an operator. Returns the key itself only when the
 * taxonomy has no entry for it — a dependency pointing at an attribute the
 * source catalog doesn't publish, which must degrade rather than throw. For
 * Google the catalog is static and shared across tenants, so this is a
 * defensive fallback rather than something tenant config drift can trigger.
 */
export function labelForKey(groups: readonly EnrichmentAttributeGroup[], key: string): string {
  for (const group of groups) {
    for (const attribute of group.attributes) {
      if (attribute.key === key) return attribute.label;
    }
  }
  return key;
}
